import { NgZone } from '@angular/core';
import { Observable } from 'rxjs';
import { MacroActionValueType, MacroDefinitionDto } from 'webcommon/shared';
import { AutoCompleterSetupDto } from './AutoCompleterSetupDto';
import { ApBaseClass } from 'webcommon/shared';
import { MacroService } from '../ap-macro/services/MacroService';
import { MacroActionValueDtoExtended } from '../ap-macro/MacroActionValueDtoExtended';
import { ContentCharacterLengthService } from '../content-character-length/index';

export class AutoCompleter extends ApBaseClass {
  constructor(
    private macroService: MacroService,
    private contentCharacterLengthService: ContentCharacterLengthService | null = null) { super(); }

    public static create(macroService: MacroService, contentCharacterLengthService: ContentCharacterLengthService | null = null) {
      return new AutoCompleter(macroService, contentCharacterLengthService);
    }

  public addAutoCompleter(
    editor: any, // look at reference at tinymce.d.ts
    ngzone: NgZone,
    setupDto: AutoCompleterSetupDto) {

    if (!this.macroService) return;
    const macroService: MacroService = this.macroService;
    const ngZone = ngzone;
    const autoCompleterSetupDto: AutoCompleterSetupDto = setupDto;
    const contentCharacterLengthService: ContentCharacterLengthService | null = this.contentCharacterLengthService;

    editor.ui.registry.addAutocompleter(autoCompleterSetupDto.macroConfigDto.name, {
      columns: 1,   // lower number means searching sooner - but more lookups as we go
      minChars: 0,  // must be 1 for text-based results
      trigger: '.',  // updated ch to trigger
      fetch(pattern: any, maxResults: any) {
        const matchingResults = macroService.getMatchingResults(pattern, setupDto.macroConfigDto.macroInterfaces);
        return new Promise((resolve: (arg0: any) => void) => {
          resolveResults(resolve, matchingResults.slice(0, maxResults));
        });
      },
      onAction(autocompleteApi: { hide: () => void }, rng: any, id: any) {
        setContent(rng, id);
        autocompleteApi.hide();
      },
    });

    function getContentByUXId(id: any): Observable<MacroActionValueDtoExtended> {
      return macroService.getContentByUXId(id, setupDto.macroConfigDto);
    }

    function containsHtml(s: string | null): boolean {
      // const regex = new RegExp('<[a-z][\s\S]*>');
      //     return regex.test(s);
      if (!s) return false;
      const doc = new DOMParser().parseFromString(s, 'text/html');
      return Array.from(doc.body.childNodes).some((node) => node.nodeType === 1);
    }

    function setContent(rng: any, id: any) {
      editor.selection.setRng(rng);

      getContentByUXId(id).subscribe(
          (value) => onContentAvailable(value),
          () => { /* error handling */ },
        );
    }

    function onContentAvailable(value: MacroActionValueDtoExtended): void {
      switch (value.Action) {
        case MacroActionValueType.ShowValue:
        case MacroActionValueType.ShowValueNotifyMedication: {
          if (contentCharacterLengthService) {
            const charLength = contentCharacterLengthService.getTextContentLength(value.Value);
            if (contentCharacterLengthService.willContentExceedMaxCharacterLength(charLength)) {
              setupDto.onWarningAdded.next(getMacroActionValueMaxCountExceeded());
              return;
            }
          }

          value.Action = MacroActionValueType.ShowValue;
          if (containsHtml(value.Value)) {
            editor.insertContent(value.Value);
          } else {
            const s = value.Value?.replace(/\r?\n/g, '<br />');
            editor.insertContent(s);
          }
          break;
        }
        case MacroActionValueType.ShowWarning: {
          value.Action = MacroActionValueType.ShowWarning;
          setupDto.onWarningAdded.next(value);
          editor.insertContent('');
          break;
        }
        case MacroActionValueType.PickDate: {
          if (contentCharacterLengthService) {
            const charLength = contentCharacterLengthService.getTextContentLength(value.Value);
            if (contentCharacterLengthService.willContentExceedMaxCharacterLength(charLength)) {
              setupDto.onWarningAdded.next(getMacroActionValueMaxCountExceeded());
              return;
            }
          }

          value.Action = MacroActionValueType.PickDate;
          editor.insertContent(value.Value);
          break;
        }
        case MacroActionValueType.PersistentData: {
          editor.insertContent('');
          const content = editor.getContent();  // get text editor content
          // clear auto complete text in editor
          const persistentData: MacroActionValueDtoExtended = {
            Action: MacroActionValueType.PersistentData,
            Value: content,
            MacroResultId: '',
            MacroInterfaces: value.MacroInterfaces,
          };
          // Give content of text editor to service, service will locate [[...]]
          // extract text and save the extracted text. Will return content minus
          // the [[..]] characters.
          value = macroService.savePersistentData(persistentData);
          // This code needs to be refactored!
          switch (value.Action) {
            case MacroActionValueType.ShowValue: {
              editor.setContent(value.Value); // replace content in text editor
              if (value.MacroInterfaces && value.FilteredValue) {
                value.MacroInterfaces.setPtext(value.FilteredValue);
              }
              value.Value = '';
              break;
            }
            case MacroActionValueType.ShowWarning: {
              value.Action = MacroActionValueType.ShowWarning;
              editor.insertContent('');
              break;
            }
            default: {
              value.Action = MacroActionValueType.None;
              editor.insertContent('');
            }
          }
          break;
        }
        default: {
          value.Action = MacroActionValueType.None;
          editor.insertContent('');
        }
      }
      saveValue(value);
    }

    function getMacroActionValueMaxCountExceeded(): MacroActionValueDtoExtended {
      return {
        Value: 'The content you are inserting would exceed the maximum number of characters allowed.',
        Action: MacroActionValueType.ShowWarning,
      };
    }

    function saveValue(value: MacroActionValueDtoExtended) {
      ngZone.run(() => {
        autoCompleterSetupDto.macroActionValue = value;
      });
    }

    function resolveResults(resolve: (arg0: any) => void, matchingResults: any) {
      // Convert to list of objects expected by the AutoCompleter expects.
      const results = matchingResults.map((m: MacroDefinitionDto) => {
        return {
          type: 'autocompleteitem',
          icon: null,
          text: m.Name,
          value: m.TransientUXId,  // because of macros with parameters, m.Id and each
                                   // m.SelectionIds are not guaranteed unique; TransientUXId is.
        };
      });
      if (results.length > 0) {
        results.sort((l: any, r: any) => sortText(l, r));
      }
      resolve(results);
    }

    function sortText(l: any, r: any) {
      const x = l.text.toLowerCase();
      const y = r.text.toLowerCase();
      if (x < y) {
        return -1;
      }
      if (x > y) {
        return 1;
      }
      return 0;
    }
  }
}
